import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Headphones, ScrollText, CheckCircle2 } from "lucide-react";

export default function AdminTranscripts() {
  const [tests, setTests] = useState([]);
  const [slug, setSlug] = useState("");
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/exams?skill=listening").then((r) => setTests(r.data.exams || [])).catch(() => toast.error("Could not load listening tests."));
  }, []);

  const loadQuestions = useCallback((s) => {
    if (!s) return;
    setLoading(true);
    api.get(`/admin/exams/${s}/questions`).then((r) => {
      setQuestions(r.data.questions || []);
      const d = {};
      (r.data.questions || []).forEach((q) => { d[q.id] = { transcript: q.transcript || "", answer_highlight: q.answer_highlight || "" }; });
      setDrafts(d);
    }).catch(() => toast.error("Could not load questions.")).finally(() => setLoading(false));
  }, []);

  const onPick = (s) => { setSlug(s); loadQuestions(s); };

  const save = async (qid) => {
    setSaving(qid);
    try {
      await api.patch(`/admin/exams/questions/${qid}`, drafts[qid]);
      toast.success("Saved");
      setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, ...drafts[qid] } : q)));
    } catch { toast.error("Save failed"); }
    finally { setSaving(""); }
  };

  const upd = (qid, field, val) => setDrafts((d) => ({ ...d, [qid]: { ...d[qid], [field]: val } }));

  return (
    <div data-testid="admin-transcripts-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> Back to Admin</Link>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-3 flex items-center gap-3"><ScrollText className="h-7 w-7 text-primary" /> Listening transcripts</h1>
      <p className="text-muted-foreground mt-2">Review and correct the AI-generated transcripts and answer highlights for each listening question.</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="text-sm font-semibold">Test:</label>
        <select data-testid="transcript-test-select" value={slug} onChange={(e) => onPick(e.target.value)}
          className="h-11 rounded-xl border border-border bg-background px-4 text-sm min-w-[220px]">
          <option value="">Select a listening test…</option>
          {tests.map((t) => <option key={t.slug} value={t.slug}>{t.title}</option>)}
        </select>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
      </div>

      {slug && !loading && (
        <div className="mt-6 space-y-4">
          {questions.map((q) => {
            const d = drafts[q.id] || { transcript: "", answer_highlight: "" };
            const hlOk = !d.answer_highlight || (d.transcript || "").includes(d.answer_highlight);
            return (
              <div key={q.id} className="rounded-2xl bg-card border border-border p-4 sm:p-5" data-testid={`tq-${q.number}`}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span className="h-6 w-6 grid place-items-center rounded-full emerald-surface text-white text-xs font-bold">{q.number}</span>
                  <span className="text-muted-foreground">{q.question_text}</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Answer: {q.correct_answer}</span>
                </div>
                <div className="mt-3 grid gap-3">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Transcript</label>
                    <textarea data-testid={`transcript-input-${q.number}`} value={d.transcript} onChange={(e) => upd(q.id, "transcript", e.target.value)}
                      rows={4} className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm leading-relaxed" placeholder="Transcript of the audio…" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground">Answer highlight (must be an exact substring of the transcript)</label>
                    <input data-testid={`highlight-input-${q.number}`} value={d.answer_highlight} onChange={(e) => upd(q.id, "answer_highlight", e.target.value)}
                      className={`mt-1 w-full rounded-xl border bg-background p-3 text-sm ${hlOk ? "border-border" : "border-destructive"}`} placeholder="Phrase in the transcript that proves the answer" />
                    {!hlOk && <p className="text-xs text-destructive mt-1">This phrase is not found verbatim in the transcript — it won't highlight.</p>}
                  </div>
                  <div className="flex justify-end">
                    <button onClick={() => save(q.id)} disabled={saving === q.id} data-testid={`save-transcript-${q.number}`}
                      className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60">
                      {saving === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {questions.length === 0 && <p className="text-muted-foreground"><Headphones className="h-4 w-4 inline mr-1" /> No questions found for this test.</p>}
        </div>
      )}
    </div>
  );
}
